import type {ScanMeasurement} from "utils/funnel";
import {makeAutoObservable} from "mobx";
import {translate} from "i18n/translator";
import {reprNetworkError} from "utils";
import {EditorStore} from "stores/editorStore";
import {RequestStatusStore} from "stores/requestStatusStore";
import {waitTaskResult} from "requests";
import type {RootStore} from "stores/rootStore";
import type {PlagiarismData, PlagiarismSource, Sentence} from "types";
import {gtmAppScanSuccess} from "utils/gtm";
import {logEvent} from "analytics";
import type {CertificateSource} from "utils/certificate";

function plagiarismDataToTexts(plagiarismData: PlagiarismData) {
  const plagiarizedSentences: Array<{ text: string; probability: number }> = [];
  for (const result of plagiarismData.results) {
    for (const score of result.matches) {
      plagiarizedSentences.push({
        text: score.text_sentence,
        probability: score.match_score,
      });
    }
  }
  return plagiarizedSentences;
}

export interface GroupScoreBlock {
  probability: number;
  texts: string[];
  shortLink: string;
  sentence: string[];
}

export interface GroupedResult {
  shortLink: string;
  title: string;
  link: string;
  scores: GroupScoreBlock[];
  score: number;
}

export class PlagiarismStore {
  root: RootStore;
  activeSourceId: number | null = null;
  activeMatchIdx: number = 0;
  originalText: string = "";
  score: number | null = null;
  plagiarismData: PlagiarismData | null = null;
  /** The analysis the result on screen came from — its certificate goes there. */
  analysisUuid: string | null = null;
  editor: EditorStore;
  requestStatus: RequestStatusStore = new RequestStatusStore();
  loadingStartedAt: number | null = null;

  constructor(root: RootStore) {
    this.root = root;
    this.editor = new EditorStore(root);
    makeAutoObservable(this);
  }

  get plagiarismSources(): PlagiarismSource[] {
    if (!this.plagiarismData) return [];
    return this.plagiarismData.results;
  }

  setScore(score: number | null) {
    this.score = score;
  }

  setAnalysisUuid(uuid: string) {
    this.analysisUuid = uuid;
  }

  /** What the result on screen offers to download (see CertificateStore). */
  get certificateSource(): CertificateSource {
    return {analysisUuid: this.analysisUuid, plagiarismScore: this.plagiarismData?.score, pending: this.isLoading};
  }

  get isLoading() {
    return this.requestStatus.isLoading;
  }

  get haveResults() {
    return !this.isLoading && !!this.plagiarismData
  }

  setPlagiarismSegments(segments: Sentence[]) {
    this.editor.setSegments(segments);
  }

  async makeRequest(task_id: number) {
    const signal = this.requestStatus.startRequest();
    return await waitTaskResult(task_id, signal);
  }

  async waitPlagiarismResult(task_id: number, measurement: ScanMeasurement = {}) {
        const startedAt = performance.now();
    this.setIsLoading(true);
    try {
      const response: any = await this.makeRequest(task_id);
      let data = response.data as PlagiarismData;
      const segments = this.mapPlagiarismSegments(data.text, data);
      this.setOriginalText(data.text);
      this.setIsLoading(false);
      this.setPlagiarismData(data);
      this.editor.setSegments(segments);
      logEvent(gtmAppScanSuccess({...measurement, task_id, elapsed_ms: Math.round(performance.now() - startedAt), scan_type: 'plagiarism', scan_result: String(data.score), page_type: 'app_scan', entrypoint: 'plagiarism_result'}));
    } catch (e: any) {
      console.warn("[Plagiarism error]", e);
      this.setIsLoading(false);
      const isCanceled = this.requestStatus.isCanceledError(e);
      if (isCanceled) {
        this.root.setError(null);
      } else {
        const errorInfo = reprNetworkError(e);
        // No limit branch here, deliberately: this is the result poll, which cannot carry a
        // limit code (InputStore.isLimitError says why). The paywall for this scan belongs to
        // the request that reserves the words — inputStore's POST /scan handler.
        this.root.setError({
          ...errorInfo,
          message: () => translate('scan-error-plagiarism', {details: errorInfo.message}),
        });
      }
      throw e;
    } finally {
      this.requestStatus.finishRequest();
    }
  }

  setIsLoading(value: boolean) {
    // POST /scan and result polling share one clock; view remounts do not restart it.
    if (value && !this.isLoading) this.loadingStartedAt = performance.now();
    if (!value) this.loadingStartedAt = null;
    this.requestStatus.isLoading = value;
  }

  setPlagiarismData(data: PlagiarismData | null) {
    if (!data) {
      this.plagiarismData = null;
      return;
    }
    data.results.sort((a, b) => b.score - a.score);
    data.results.forEach(
      (result) => {
        result.matches.sort((a, b) =>
          data.text.indexOf(a.text_sentence) - data.text.indexOf(b.text_sentence)
        );
      }
    )
    this.plagiarismData = data;
    this.setScore(data.score);
  }

  setActiveSource(id: number | null) {
    this.activeSourceId = id;
  }

  incrementActiveSubgroup() {
    const prev = this.activeMatchIdx;
    this.activeMatchIdx = Math.min(prev + 1, this.activeSource?.matches?.length || 0);
  }

  decrementActiveSubgroup() {
    const prev = this.activeMatchIdx;
    this.activeMatchIdx = Math.max(0, prev - 1);
  }

  get activeSource(): PlagiarismSource | null {
    return this.plagiarismSources.find((result) => result.id === this.activeSourceId) || null;
  }

  setOriginalText(text: string) {
    this.originalText = text;
  }

  setSelectedSentences(sentences: string[]) {
    this.editor.selectedSentences = sentences;
  }

  setActiveMatch(index: number) {
    this.activeMatchIdx = index;
  }

  clear() {
    this.requestStatus.cancelOngoingRequest();
    this.loadingStartedAt = null;
    this.plagiarismData = null;
    this.score = null;
    this.originalText = "";
    this.analysisUuid = null;

    this.activeSourceId = null;
    this.activeMatchIdx = 1;
    this.editor.clear();
  }

  mapPlagiarismSegments(text: string, plagiarismData: PlagiarismData) {
    const plagiarizedTexts = plagiarismDataToTexts(plagiarismData);

    // Deduplicate by text, keeping highest probability
    const uniqueMap = new Map<string, number>();
    for (const plag of plagiarizedTexts) {
      const existing = uniqueMap.get(plag.text);
      if (existing === undefined || plag.probability > existing) {
        uniqueMap.set(plag.text, plag.probability);
      }
    }

    const ranges: Array<{ start: number; end: number; probability: number }> = [];
    for (const [sentence, probability] of uniqueMap) {
      let searchFrom = 0;
      while (searchFrom < text.length) {
        const idx = text.indexOf(sentence, searchFrom);
        if (idx === -1) break;
        ranges.push({ start: idx, end: idx + sentence.length, probability });
        searchFrom = idx + sentence.length;
      }
    }
    ranges.sort((a, b) => a.start - b.start);

    const segments: Sentence[] = [];
    let lastEnd = 0;

    for (const range of ranges) {
      if (range.end <= lastEnd) continue;
      const effectiveStart = Math.max(range.start, lastEnd);

      if (effectiveStart > lastEnd) {
        const segmentText = text.slice(lastEnd, effectiveStart);
        if (segmentText.trim().length > 0) {
          segments.push({
            text: segmentText,
            score: 0,
            tone: "original",
          });
        }
      }

      const segmentText = text.slice(effectiveStart, range.end);
      if (segmentText.trim().length > 0) {
        segments.push({
          text: segmentText,
          score: range.probability * 100,
          tone: "plagiarized",
        });
      }

      lastEnd = range.end;
    }

    if (lastEnd < text.length) {
      const segmentText = text.slice(lastEnd);
      if (segmentText.trim().length > 0) {
        segments.push({
          text: segmentText,
          score: 0,
          tone: "original",
        });
      }
    }

    if (segments.length === 0) {
      return [{
        text: text,
        score: 0,
        tone: "original" as const,
      }];
    }

    return segments;
  }
}
